import React, { useState, useCallback } from 'react';
import { Box, Typography, Grid, Paper, Button, Switch, FormControlLabel } from "@mui/material";
import WorkOutlineIcon from "@mui/icons-material/WorkOutline";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { DynamicFieldAdvanced } from '../../../../../../components/common/input/DynamicFieldAdvanced';
import { useSelector, useDispatch } from 'react-redux';
import { setEmploymentList, updateEmploymentItem, setStepCompletion } from '../../../../../../store';
import { saveEmployeeExperience } from '../../../../../../api/employeeApi';
import { useNotification } from '../../../../../../hooks/useNotification';


// Helper for file upload
const FileUploadField = ({ label, name, files, onChange, required, error, errorText }) => {
  const handleFileChange = (e) => {
    const fileList = Array.from(e.target.files);
    onChange(name, fileList);
  };

  // Check if files exist and are not empty
  const hasFiles = files && files.length > 0 && files[0] !== '';

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="body2" sx={{ fontWeight: 700, color: error ? '#EF4444' : '#374151', fontSize: '0.825rem', mb: 0.5 }}>
        {label}{required && ' *'}
      </Typography>

      <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
        {!hasFiles ? (
          <Button
            component="label"
            variant="outlined"
            size="small"
            startIcon={<CloudUploadIcon />}
            sx={{
              borderColor: error ? '#EF4444' : 'rgba(117, 34, 209, 0.3)',
              color: error ? '#EF4444' : '#7522D1',
              backgroundColor: error ? 'rgba(239, 68, 68, 0.02)' : 'rgba(117, 34, 209, 0.02)',
              fontWeight: 700,
              '&:hover': {
                borderColor: error ? '#EF4444' : '#7522D1',
                backgroundColor: error ? 'rgba(239, 68, 68, 0.06)' : 'rgba(117, 34, 209, 0.06)',
              }
            }}
          >
            Choose files
            <input type="file" hidden multiple={name === 'payslipFiles'} onChange={handleFileChange} />
          </Button>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
            {files.map((file, i) => (
              <Box
                key={i}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: '20px',
                  bgcolor: 'rgba(16, 185, 129, 0.06)',
                  border: '1px solid rgba(16, 185, 129, 0.15)',
                  boxShadow: '0 2px 8px rgba(16, 185, 129, 0.02)',
                }}
              >
                {typeof file === 'string' && file !== '' ? (
                  <Typography
                    variant="caption"
                    component="a"
                    href={file}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: "#059669",
                      cursor: "pointer",
                      textDecoration: "none",
                      fontWeight: 700,
                      fontSize: '0.75rem',
                      "&:hover": { textDecoration: "underline" }
                    }}
                  >
                    View Document
                  </Typography>
                ) : (
                  <Typography variant="caption" sx={{ color: '#065F46', fontWeight: 700, fontSize: '0.75rem' }}>{file.name}</Typography>
                )}
                
                <Button
                  component="label"
                  size="small"
                  sx={{
                    p: 0,
                    minWidth: 'auto',
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    color: '#7522D1',
                    textTransform: 'none',
                    '&:hover': { background: 'none', textDecoration: 'underline' }
                  }}
                >
                  Change
                  <input type="file" hidden onChange={handleFileChange} />
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {errorText && (
        <Typography variant="caption" sx={{ color: '#EF4444', display: 'block', mt: 0.5, fontWeight: 600 }}>
          {errorText}
        </Typography>
      )}
    </Box>
  );
};

const ReferenceFields = ({ references, onChange, onAdd, onRemove, errors, touched, idx }) => (
  <Box sx={{ mb: 2 }}>
    <Typography variant="subtitle2" sx={{ mb: 1 }}>References</Typography>
    {references.map((ref, rIdx) => (
      <Grid container spacing={2} key={rIdx} alignItems="center">
        <Grid item xs={12} md={3}>
          <DynamicFieldAdvanced
            type="text"
            label="Name"
            name={`ref_name_${rIdx}`}
            value={ref.name || ""}
            onChange={(n, v) => onChange(rIdx, 'name', v)}
            onBlur={() => { }}
            error={touched && touched[`ref_name_${idx}_${rIdx}`] && !!errors[`ref_name_${idx}_${rIdx}`]}
            errorText={touched && touched[`ref_name_${idx}_${rIdx}`] ? errors[`ref_name_${idx}_${rIdx}`] : ""}
            required={false}
          />
        </Grid>
        <Grid item xs={12} md={3}>
          <DynamicFieldAdvanced
            type="text"
            label="Phone No."
            name={`ref_phone_${rIdx}`}
            value={ref.phone || ""}
            onChange={(n, v) => onChange(rIdx, 'phone', v)}
            onBlur={() => { }}
            error={touched && touched[`ref_phone_${idx}_${rIdx}`] && !!errors[`ref_phone_${idx}_${rIdx}`]}
            errorText={touched && touched[`ref_phone_${idx}_${rIdx}`] ? errors[`ref_phone_${idx}_${rIdx}`] : ""}
            required={false}
            numericOnly
            inputProps={{ maxLength: 10 }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <DynamicFieldAdvanced
            type="email"
            label="Email ID"
            name={`ref_email_${rIdx}`}
            value={ref.email || ""}
            onChange={(n, v) => onChange(rIdx, 'email', v)}
            onBlur={() => { }}
            error={touched && touched[`ref_email_${idx}_${rIdx}`] && !!errors[`ref_email_${idx}_${rIdx}`]}
            errorText={touched && touched[`ref_email_${idx}_${rIdx}`] ? errors[`ref_email_${idx}_${rIdx}`] : ""}
            required={false}
          />
        </Grid>
        <Grid item xs={12} md={2}>
          {references.length > 1 && (
            <Button color="error" onClick={() => onRemove(rIdx)} size="small">Remove</Button>
          )}
        </Grid>
      </Grid>
    ))}
    <Button onClick={onAdd} size="small" sx={{ mt: 1 }}>Add Reference</Button>
  </Box>
);

const EmploymentItem = React.memo(({ emp, idx, errors, touched, onFieldChange, onFieldBlur, onRemove, canRemove }) => {
  const handleChange = useCallback((name, value) => {
    onFieldChange(idx, name, value);
  }, [idx, onFieldChange]);

  const handleBlur = useCallback((name) => {
    onFieldBlur(idx, name);
  }, [idx, onFieldBlur]);

  const handleRemoveClick = useCallback(() => {
    onRemove(idx);
  }, [idx, onRemove]);

  // Reference logic
  const handleReferenceChange = (rIdx, field, value) => {
    const updatedRefs = emp.references.map((ref, i) => i === rIdx ? { ...ref, [field]: value } : ref);
    handleChange('references', updatedRefs);
  };
  const handleAddReference = () => {
    handleChange('references', [...(emp.references || []), { name: '', phone: '', email: '' }]);
  };
  const handleRemoveReference = (rIdx) => {
    if ((emp.references || []).length > 1) {
      handleChange('references', emp.references.filter((_, i) => i !== rIdx));
    }
  };

  // File upload logic
  const handleFileChange = (name, files) => {
    handleChange(name, files);
  };

  return (
    <Box sx={{ mb: 4, border: '1px solid rgba(117,34,209,0.08)', borderRadius: '12px', p: 3, position: 'relative', bgcolor: '#FAF9FB' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', mr: 2 }}>
            Employment Details #{idx + 1}
          </Typography>
        </Box>
        {canRemove && (
          <Button
            variant="outlined"
            color="error"
            size="small"
            onClick={handleRemoveClick}
            sx={{
              borderColor: 'rgba(239, 68, 68, 0.4)',
              color: '#EF4444',
              '&:hover': {
                borderColor: '#EF4444',
                backgroundColor: 'rgba(239, 68, 68, 0.04)',
              }
            }}
          >
            Remove
          </Button>
        )}
      </Box>

      <Grid container spacing={3}>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Company Name"
            name="companyName"
            value={emp.companyName || ""}
            onChange={handleChange}
            onBlur={() => handleBlur("companyName")}
            error={touched[`companyName_${idx}`] && !!errors[`companyName_${idx}`]}
            errorText={touched[`companyName_${idx}`] ? errors[`companyName_${idx}`] : ""}
            required
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="text"
            label="Designation"
            name="designation"
            value={emp.designation || ""}
            onChange={handleChange}
            onBlur={() => handleBlur("designation")}
            error={touched[`designation_${idx}`] && !!errors[`designation_${idx}`]}
            errorText={touched[`designation_${idx}`] ? errors[`designation_${idx}`] : ""}
            required
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="number"
            label="CTC (per annum)"
            name="ctc"
            value={emp.ctc || ""}
            onChange={handleChange}
            onBlur={() => handleBlur("ctc")}
            error={touched[`ctc_${idx}`] && !!errors[`ctc_${idx}`]}
            errorText={touched[`ctc_${idx}`] ? errors[`ctc_${idx}`] : ""}
            required
            placeholder="Enter amount in lakhs"
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="date"
            label="Date of Joining"
            name="joiningDate"
            value={emp.joiningDate || ""}
            onChange={handleChange}
            onBlur={() => handleBlur("joiningDate")}
            error={touched[`joiningDate_${idx}`] && !!errors[`joiningDate_${idx}`]}
            errorText={touched[`joiningDate_${idx}`] ? errors[`joiningDate_${idx}`] : ""}
            required
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <DynamicFieldAdvanced
            type="date"
            label="Date of Relieving"
            name="relievingDate"
            value={emp.relievingDate || ""}
            onChange={handleChange}
            onBlur={() => handleBlur("relievingDate")}
            error={touched[`relievingDate_${idx}`] && !!errors[`relievingDate_${idx}`]}
            errorText={touched[`relievingDate_${idx}`] ? errors[`relievingDate_${idx}`] : ""}
            required
          />
        </Grid>
        <Grid item size={{ xs: 12, }}>
          <DynamicFieldAdvanced
            type="textarea"
            label="Address"
            name="address"
            value={emp.address || ""}
            onChange={handleChange}
            onBlur={() => handleBlur("address")}
            error={touched[`address_${idx}`] && !!errors[`address_${idx}`]}
            errorText={touched[`address_${idx}`] ? errors[`address_${idx}`] : ""}
            minRows={2}
            required
          />
        </Grid>

        {/* Only show these fields for all employment entries */}
        <>
          {/* HR Team */}

          <Grid item size={{ xs: 12, md: 4 }}>
            <DynamicFieldAdvanced
              type="text"
              label="HR Name"
              name="hrName"
              value={emp.hrName || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("hrName")}
              error={touched[`hrName_${idx}`] && !!errors[`hrName_${idx}`]}
              errorText={touched[`hrName_${idx}`] ? errors[`hrName_${idx}`] : ""}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <DynamicFieldAdvanced
              type="text"
              label="HR Phone No."
              name="hrPhone"
              value={emp.hrPhone || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("hrPhone")}
              error={touched[`hrPhone_${idx}`] && !!errors[`hrPhone_${idx}`]}
              errorText={touched[`hrPhone_${idx}`] ? errors[`hrPhone_${idx}`] : ""}
              numericOnly
              inputProps={{ maxLength: 10 }}
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <DynamicFieldAdvanced
              type="email"
              label="HR Email ID"
              name="hrEmail"
              value={emp.hrEmail || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("hrEmail")}
              error={touched[`hrEmail_${idx}`] && !!errors[`hrEmail_${idx}`]}
              errorText={touched[`hrEmail_${idx}`] ? errors[`hrEmail_${idx}`] : ""}
            />
          </Grid>
          {/* Reporting Manager */}
          <Grid item size={{ xs: 12, md: 4 }}>
            <DynamicFieldAdvanced
              type="text"
              label="Reporting Manager Name"
              name="managerName"
              value={emp.managerName || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("managerName")}
              error={touched[`managerName_${idx}`] && !!errors[`managerName_${idx}`]}
              errorText={touched[`managerName_${idx}`] ? errors[`managerName_${idx}`] : ""}
              required
            />
          </Grid>
          <Grid item size={{ xs: 12, md: 4 }}>
            <DynamicFieldAdvanced
              type="text"
              label="Reporting Manager Phone No."
              name="managerPhone"
              value={emp.managerPhone || ""}
              onChange={handleChange}
              onBlur={() => handleBlur("managerPhone")}
              error={touched[`managerPhone_${idx}`] && !!errors[`managerPhone_${idx}`]}
              errorText={touched[`managerPhone_${idx}`] ? errors[`managerPhone_${idx}`] : ""}
              required
              numericOnly
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          {/* References */}
          <Grid item size={{ xs: 12 }}>
            <ReferenceFields
              references={emp.references || [{ name: '', phone: '', email: '' }]}
              onChange={handleReferenceChange}
              onAdd={handleAddReference}
              onRemove={handleRemoveReference}
              errors={errors}
              touched={touched}
              idx={idx}
            />
          </Grid>

          <Grid item size={{ xs: 12, md: 6 }}>
            <FileUploadField
              label="Upload Last 3 Months Payslips*"
              name="payslipFiles"
              files={emp.payslipFiles || []}
              onChange={handleFileChange}
              required
              error={touched[`payslipFiles_${idx}`] && !!errors[`payslipFiles_${idx}`]}
              errorText={touched[`payslipFiles_${idx}`] ? errors[`payslipFiles_${idx}`] : ""}
            />
          </Grid>
        </>
        {/* Always show these fields */}
        <Grid item size={{ xs: 12, md: 6 }}>
          <FileUploadField
            label="Upload Experience Letter"
            name="experienceLetterFiles"
            files={emp.experienceLetterFiles || []}
            onChange={handleFileChange}
          />
        </Grid>
        <Grid item size={{ xs: 12, md: 6 }}>
          <FileUploadField
            label="Upload Relieving Letter"
            name="relievingLetterFiles"
            files={emp.relievingLetterFiles || []}
            onChange={handleFileChange}
          />
        </Grid>
      </Grid>
    </Box>
  );
});

EmploymentItem.displayName = 'EmploymentItem';

const EmploymentDetails = ({ onNext, onBack, employeeId, onRefetch }) => {
  const dispatch = useDispatch();
  const { showNotification, Notification } = useNotification();
  const employmentList = useSelector((state) => state.basicDetails.employmentList) || [];
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [isFresher, setIsFresher] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Helper function to validate email
  const validateEmail = (email) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Helper function to validate phone number
  const validatePhone = (phone) => {
    if (!phone) return false;
    const cleanedPhone = phone.replace(/[\s-]/g, '');
    return /^[0-9]{10}$/.test(cleanedPhone);
  };

  const validate = () => {
    const newErrors = {};

    employmentList.forEach((emp, idx) => {
      // Always required fields
      if (!emp.companyName?.trim()) {
        newErrors[`companyName_${idx}`] = 'Company name is required';
      }
      if (!emp.designation?.trim()) {
        newErrors[`designation_${idx}`] = 'Designation is required';
      }
      if (!emp.ctc) {
        newErrors[`ctc_${idx}`] = 'CTC is required';
      }
      if (!emp.joiningDate) {
        newErrors[`joiningDate_${idx}`] = 'Joining date is required';
      }
      if (!emp.relievingDate) {
        newErrors[`relievingDate_${idx}`] = 'Relieving date is required';
      } else if (emp.joiningDate && new Date(emp.relievingDate) < new Date(emp.joiningDate)) {
        newErrors[`relievingDate_${idx}`] = 'Relieving date cannot be before joining date';
      }
      if (!emp.address?.trim()) {
        newErrors[`address_${idx}`] = 'Address is required';
      }

      // HR fields (optional)
      if (emp.hrPhone?.trim() && !validatePhone(emp.hrPhone)) {
        newErrors[`hrPhone_${idx}`] = 'HR Phone must be exactly 10 digits';
      }
      if (emp.hrEmail?.trim() && !validateEmail(emp.hrEmail)) {
        newErrors[`hrEmail_${idx}`] = 'Please enter a valid email address';
      }

      // References (optional)
      if (emp.references && emp.references.length > 0) {
        emp.references.forEach((ref, rIdx) => {
          if (ref.phone?.trim() && !validatePhone(ref.phone)) {
            newErrors[`ref_phone_${idx}_${rIdx}`] = 'Reference phone must be exactly 10 digits';
          }
          if (ref.email?.trim() && !validateEmail(ref.email)) {
            newErrors[`ref_email_${idx}_${rIdx}`] = 'Please enter a valid email address';
          }
        });
      }
      // Payslips required
      if (!emp.payslipFiles || emp.payslipFiles.length === 0) {
        newErrors[`payslipFiles_${idx}`] = 'Payslips are required';
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleFieldChange = useCallback((idx, name, value) => {
    const updatedList = employmentList.map((item, i) =>
      i === idx ? { ...item, [name]: value } : item
    );
    dispatch(setEmploymentList(updatedList));
    // Clear error for this field
    const errorKey = `${name}_${idx}`;
    setErrors((prev) => {
      if (prev[errorKey]) {
        const newErrors = { ...prev };
        delete newErrors[errorKey];
        return newErrors;
      }
      return prev;
    });
  }, [employmentList, dispatch]);

  const handleBlur = useCallback((idx, name) => {
    setTouched(prev => ({ ...prev, [`${name}_${idx}`]: true }));
  }, []);

  const handleAddEmployment = useCallback(() => {
    const updatedList = [
      ...employmentList,
      {
        companyName: "",
        designation: "",
        ctc: "",
        joiningDate: "",
        relievingDate: "",
        address: "",
        hrName: "",
        hrPhone: "",
        hrEmail: "",
        managerName: "",
        managerPhone: "",
        references: [
          { name: '', phone: '', email: '' }
        ],
        payslipFiles: [],
        experienceLetterFiles: [],
        relievingLetterFiles: [],
        isLatest: false,
      },
    ];
    dispatch(setEmploymentList(updatedList));
  }, [employmentList, dispatch]);

  const handleRemoveEmployment = useCallback((idx) => {
    if (employmentList.length > 1) {
      const updatedList = employmentList.filter((_, i) => i !== idx);
      dispatch(setEmploymentList(updatedList));
    }
  }, [employmentList, dispatch]);

  const handleSubmit = async () => {

    // If user is a fresher, skip validation and proceed
    if (isFresher) {
      // Mark Previous Employment step as completed for freshers
      dispatch(setStepCompletion({ step: 'previousEmployment', isCompleted: true }));
      onNext({ isFresher });
      return;
    }

    // Clear previous errors
    setErrors({});

    // Mark all fields as touched for validation visibility
    const touchedFields = {};
    employmentList.forEach((emp, idx) => {
      touchedFields[`companyName_${idx}`] = true;
      touchedFields[`designation_${idx}`] = true;
      touchedFields[`ctc_${idx}`] = true;
      touchedFields[`joiningDate_${idx}`] = true;
      touchedFields[`relievingDate_${idx}`] = true;
      touchedFields[`address_${idx}`] = true;
      touchedFields[`hrName_${idx}`] = true;
      touchedFields[`hrPhone_${idx}`] = true;
      touchedFields[`hrEmail_${idx}`] = true;
      touchedFields[`managerName_${idx}`] = true;
      touchedFields[`managerPhone_${idx}`] = true;
      touchedFields[`payslipFiles_${idx}`] = true;
      if (emp.references) {
        emp.references.forEach((_, rIdx) => {
          touchedFields[`ref_name_${idx}_${rIdx}`] = true;
          touchedFields[`ref_phone_${idx}_${rIdx}`] = true;
          touchedFields[`ref_email_${idx}_${rIdx}`] = true;
        });
      }
    });
    setTouched(touchedFields);

    const isValid = validate();

    if (isValid) {
      if (!employeeId) {
        setErrors({ general: 'Employee ID is missing. Please ensure you started the process correctly.' });
        return;
      }

      setIsSubmitting(true);
      try {
        const payload = new FormData();

        // Map data for JSON body
        const experienceData = employmentList.map(emp => ({
          companyName: emp.companyName,
          designation: emp.designation,
          address: emp.address,
          ctc: emp.ctc,
          joiningDate: emp.joiningDate,
          relievingDate: emp.relievingDate,
          hrName: emp.hrName,
          hrPhone: emp.hrPhone,
          hrEmail: emp.hrEmail,
          reportingManagerName: emp.managerName,
          reportingManagerPhone: emp.managerPhone,
          references: emp.references,
          experienceLetter: emp.experienceLetter || "",
          relievingLetter: emp.relievingLetter || "",
          payslips: emp.payslips || "",
        }));

        payload.append('experience', JSON.stringify(experienceData));

        // Append files
        employmentList.forEach((emp, idx) => {
          if (emp.experienceLetterFiles?.[0]) {
            payload.append(`experienceDocument_${idx}_experienceLetter`, emp.experienceLetterFiles[0]);
          }
          if (emp.relievingLetterFiles?.[0]) {
            payload.append(`experienceDocument_${idx}_relievingLetter`, emp.relievingLetterFiles[0]);
          }
          if (emp.payslipFiles) {
            emp.payslipFiles.forEach((file, fIdx) => {
              payload.append(`experienceDocument_${idx}_payslips_${fIdx}`, file);
            });
          }
        });

        await saveEmployeeExperience(employeeId, payload);

        dispatch(setStepCompletion({ step: 'employment', isCompleted: true }));

        setErrors({});
        setTouched({});

        showNotification("Employment history saved successfully", "success");
        
        if (onRefetch) onRefetch();

        // Always navigate to next step on successful save
        onNext({ isFresher });
      } catch (error) {
        console.error('API Error:', error);
        showNotification(error.message || "Failed to update employment history", "error");
        setErrors(prev => ({ ...prev, general: typeof error === 'string' ? error : (error.message || 'Failed to save experience details') }));
      } finally {
        setIsSubmitting(false);
      }
    } else {
      showNotification("Please fill all required fields and upload mandatory documents.", "error");
    }
  };

  const handleSkip = () => {
    // Skip validation and proceed with fresher status
    onNext({ isFresher });
  };

  const NoteBox = () => (
    <Box
      sx={{
        p: 2,
        borderRadius: '10px',
        borderLeft: '4px solid #7522D1',
        bgcolor: '#F9F5FE',
        mb: 3.5,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.5,
      }}
    >
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: '#7522D1', fontSize: '0.85rem' }}>
        Experience Verification
      </Typography>
      <Typography variant="body2" sx={{ color: '#42374F', fontWeight: 500, fontSize: '0.8rem' }}>
        Please add your previous employment history. If you have no prior work experience, toggle the <strong>Fresher</strong> switch below.
      </Typography>
    </Box>
  );

  const handleFresherToggle = (e) => {
    const checked = e.target.checked;
    setIsFresher(checked);
    if (checked) {
      setErrors({});
      setTouched({});
    }
  };

  const FresherToggle = () => (
    <Box
      sx={{
        p: 2.5,
        borderRadius: '12px',
        border: "1px solid rgba(117,34,209,0.08)",
        backgroundColor: '#FCFBFE',
        mb: 3.5,
      }}
    >
      <Box sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexDirection: { xs: "column", sm: "row" },
        gap: { xs: 2, sm: 0 },
        textAlign: { xs: "center", sm: "left" }
      }}>
        <Box>
          <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#030007", mb: 0.5 }}>
            Are you a Fresher?
          </Typography>
          <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 600 }}>
            Toggle this if you have no prior work experience to bypass this section.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch
              checked={isFresher}
              onChange={handleFresherToggle}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': {
                  color: '#7522D1',
                },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                  backgroundColor: '#7522D1',
                },
              }}
            />
          }
          label=""
        />
      </Box>
    </Box>
  );

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: 'wrap',
          gap: 2,
          mb: 3.5,
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 800, color: '#030007', display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            width: 32,
            height: 32,
            borderRadius: '8px',
            bgcolor: '#F5EEFC',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}>
            <WorkOutlineIcon sx={{ color: '#7522D1', fontSize: 18 }} />
          </Box>
          Previous Employment Details
        </Typography>
        {!isFresher && (
          <Button
            variant="outlined"
            onClick={handleAddEmployment}
          >
            + Add Employment
          </Button>
        )}
      </Box>
      <FresherToggle />
      <NoteBox />

      {!isFresher && (
        <>
          {employmentList.length === 0 && (
            <Paper
              sx={{
                p: 3,
                borderRadius: 2,
                border: "1px solid #EEF0F4",
                mb: 3,
                textAlign: "center",
                backgroundColor: "#FFF9E6"
              }}
            >
              <Typography variant="body1" sx={{ color: "#D4A017" }}>
                Please add at least one employment entry to continue.
              </Typography>
            </Paper>
          )}
          {employmentList.map((emp, idx) => (
            <EmploymentItem
              key={`emp-${idx}`}
              emp={emp}
              idx={idx}
              errors={errors}
              touched={touched}
              onFieldChange={handleFieldChange}
              onFieldBlur={handleBlur}
              onRemove={handleRemoveEmployment}
              canRemove={employmentList.length > 1}
              isLatest={false}
              onToggleLatest={() => { }}
            />
          ))}
        </>
      )}
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between' }}>
        <Button
          variant="outlined"
          onClick={onBack}
          size="large"
        >
          Back
        </Button>
        <Box sx={{ display: 'flex', gap: 2 }}>
          {!isFresher && (
            <Button
              variant="outlined"
              onClick={handleSkip}
              size="large"
            >
              Skip for now
            </Button>
          )}
          <Button
            variant="contained"
            onClick={handleSubmit}
            size="large"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : (employeeId ? 'Update' : (isFresher ? 'Next' : 'Save & Next'))}
          </Button>
        </Box>
      </Box>
      <Notification />
    </Box>
  );
};

export default EmploymentDetails;
